<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';

const props = defineProps({
  from: {
    type: Number,
    required: true,
  },
  to: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const isFetching = ref(false);
const escalationData = ref(null);

const fetchEscalationData = async () => {
  try {
    isFetching.value = true;
    const response = await ReportsAPI.getEscalationDistribution({
      from: props.from,
      to: props.to,
    });
    escalationData.value = response.data;
  } catch (error) {
    useAlert(t('REPORT.BOT_ESCALATION_DISTRIBUTION_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const chartData = computed(() => {
  if (!escalationData.value) return null;

  const escalated = escalationData.value.escalated_count || 0;
  const notEscalated = escalationData.value.not_escalated_count || 0;
  return {
    segments: [
      {
        id: 'escalated',
        label: t('REPORT.ESCALATED'),
        value: escalated,
        color: 'rgb(var(--amber-9))',
      },
      {
        id: 'not-escalated',
        label: t('REPORT.NOT_ESCALATED'),
        value: notEscalated,
        color: 'rgb(var(--teal-9))',
      },
    ],
  };
});

const hasData = computed(() => {
  return (
    escalationData.value &&
    (escalationData.value.escalated_count > 0 ||
      escalationData.value.not_escalated_count > 0)
  );
});

watch(
  () => [props.from, props.to],
  () => {
    fetchEscalationData();
  }
);

onMounted(() => {
  fetchEscalationData();
});
</script>

<template>
  <div class="p-4 mb-3 rounded-md">
    <div class="text-n-slate-11">
      <span class="text-sm">
        {{ $t('REPORT.BOT_ESCALATION_DISTRIBUTION') }}
      </span>
    </div>
    <div class="mt-4 h-72">
      <woot-loading-state
        v-if="isFetching"
        class="text-xs"
        :message="$t('REPORT.LOADING_CHART')"
      />
      <div v-else-if="hasData" class="flex items-center justify-center h-72">
        <DoughnutChart
          :data="chartData"
          :aria-label="$t('REPORT.BOT_ESCALATION_DISTRIBUTION')"
        />
      </div>
      <span v-else class="text-sm text-n-slate-10">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </div>
</template>
